import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';

import { formData, IS_WEB, parseApiError } from '@utils';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useImagePicker } from '@hooks';
import { AvatarImage, IconTouchable, Loader } from '@components';
import { ToastService } from '@services';

const PICTURE_SIZE = IS_WEB ? 120 : 100;

const PICKER_OPTS = {
  mediaTypes: ImagePicker.MediaTypeOptions.Images,
  allowsEditing: true,
  width: 500,
  height: 500,
  quality: 1,
};

const Container = styled(View)({
  flexDirection: ['column', 'row'],
  justifyContent: ['center', 'flex-start'],
  alignItems: 'center',
  py: ['$7', '$8'],
});

interface ILoaderContainerStyleProps {
  size: number;
}

const LoaderContainer = styled(View)(
  (props: ILoaderContainerStyleProps) => ({
    width: props.size,
    height: props.size,
    borderRadius: props.size / 2,
    justifyContent: 'center',
    alignItems: 'center',
  }),
);

const ButtonContainer = styled(View)({
  position: 'absolute',
  width: 56,
  height: 56,
  borderRadius: 56,
  bg: '$background0',
  alignItems: 'center',
  justifyContent: 'center',
  bottom: 0,
  right: -15,
});

export const ProfileAvatar = observer(() => {
  const viewer = useViewerModel();
  const imagePicker = useImagePicker();

  const user = viewer.currentUser;
  const isLoading = viewer.uploadPhoto.inProgress;
  const isRemoveImageAllowed = user?.hasPhoto;

  async function onRemoveImage() {
    try {
      await viewer.removePhoto.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function updateAvatar() {
    try {
      const res = await imagePicker.open({
        pickerOpts: PICKER_OPTS,
        cameraOpts: PICKER_OPTS,
        isRemoveImageAllowed,
        onRemoveImage,
      });

      if (res && res.uri) {
        const data = await formData(res.uri, 'imageFile');
        await viewer.uploadPhoto.run(data);
      }
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  return (
    <Container>
      {isLoading ? (
        <LoaderContainer size={PICTURE_SIZE}>
          <Loader size="large" />
        </LoaderContainer>
      ) : (
        <AvatarImage
          size={PICTURE_SIZE}
          source={{ uri: user?.photo.big?.url }}
        />
      )}
      <ButtonContainer>
        <IconTouchable
          onPress={updateAvatar}
          name="edit-light"
          size={20}
        />
      </ButtonContainer>
    </Container>
  );
});
